/**
 * useSidebarThreadListExpansion — per-project thread-list expand/collapse state.
 *
 * Tracks which project keys have their thread list expanded (i.e., show all
 * threads instead of the 6-thread preview). Pure local state with immutable
 * Set updates — no cross-store coupling.
 *
 * Extracted from `useSidebarOrchestration` to keep the orchestration hook
 * composition-focused.
 */

import { useCallback, useState } from "react";

// ---------------------------------------------------------------------------
// Return type
// ---------------------------------------------------------------------------

export interface SidebarThreadListExpansionResult {
  readonly expandedThreadListsByProject: ReadonlySet<string>;
  readonly expandThreadListForProject: (projectKey: string) => void;
  readonly collapseThreadListForProject: (projectKey: string) => void;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useSidebarThreadListExpansion(): SidebarThreadListExpansionResult {
  const [expandedThreadListsByProject, setExpandedThreadListsByProject] = useState<
    ReadonlySet<string>
  >(() => new Set());

  const expandThreadListForProject = useCallback((projectKey: string) => {
    setExpandedThreadListsByProject((current) => {
      if (current.has(projectKey)) return current;
      const next = new Set(current);
      next.add(projectKey);
      return next;
    });
  }, []);

  const collapseThreadListForProject = useCallback((projectKey: string) => {
    setExpandedThreadListsByProject((current) => {
      if (!current.has(projectKey)) return current;
      const next = new Set(current);
      next.delete(projectKey);
      return next;
    });
  }, []);

  return {
    expandedThreadListsByProject,
    expandThreadListForProject,
    collapseThreadListForProject,
  };
}
